// O contrato do relatório especial e o que a tela e o PDF compartilham antes
// de desenhar: os blocos do Markdown agrupados em seções.
//
// Fica num arquivo sem React de propósito: o gerador de PDF e o teste dele
// rodam fora do navegador, e não têm por que carregar JSX para isso.
import { textoPuro, type Bloco, type Trecho } from '@medicov1/shared';
import type { PacienteNoPapel } from './identificacao';

export interface ResumoEspecialDTO {
  id: string;
  /** Curto, para caber num botão: "Memória depois da parada". */
  titulo: string;
  /** A pergunta que motivou o documento, como foi feita. */
  assunto: string;
  geradoEm: string;
  autor: string;
  /**
   * "pedido" é carta da família para entregar ao hospital: sai como carta, sem
   * cabeçalho de relatório nem aviso de literatura. Opcional: os antigos não têm.
   */
  tipo?: 'especial' | 'pedido';
  /** Em que grupo da aba ele aparece. Nulo nos antigos: `grupoDoEspecial` deduz. */
  grupo?: GrupoEspecial | null;
}

export type GrupoEspecial = 'entregar' | 'reabilitacao' | 'cuidados' | 'analises' | 'interno';

/**
 * Os grupos da aba Especiais, na ordem em que aparecem. Pedido do Ilton (27/09):
 * com oito documentos numa fila só de botões, ninguém achava nada — e o que é só
 * da família ficava ao lado do que se imprime para a equipe.
 */
export const GRUPOS_ESPECIAIS: { chave: GrupoEspecial; rotulo: string; descricao: string }[] = [
  { chave: 'entregar', rotulo: 'Para entregar à equipe', descricao: 'Pedidos e relatórios para imprimir e levar ao hospital.' },
  // Pedido do Ilton (05/10): o histórico para a equipe que vai cuidar da reabilitação depois da alta (Hospital Sarah).
  { chave: 'reabilitacao', rotulo: 'Para a reabilitação', descricao: 'O histórico para a equipe que vai cuidar da reabilitação depois da alta.' },
  { chave: 'cuidados', rotulo: 'Cuidados do dia a dia', descricao: 'Para quem está no plantão: remédios, horários, o que observar.' },
  { chave: 'analises', rotulo: 'Análises da família', descricao: 'Perguntas da família cruzadas com os registros e a literatura.' },
  { chave: 'interno', rotulo: 'Registro da família — não entregar', descricao: 'Só para a família. Não imprimir para a equipe.' },
];

/** O grupo de um especial. Os antigos, sem grupo: pedido vai para entregar, o resto para análises. */
export const grupoDoEspecial = (e: Pick<ResumoEspecialDTO, 'tipo' | 'grupo'>): GrupoEspecial =>
  e.grupo ?? (e.tipo === 'pedido' ? 'entregar' : 'analises');

/** Vai para médico (a equipe daqui ou a da reabilitação): identifica como o hospital, com nome completo e prontuário. */
export const vaiParaMedico = (e: Pick<ResumoEspecialDTO, 'tipo' | 'grupo'>): boolean => {
  const g = grupoDoEspecial(e);
  return g === 'entregar' || g === 'reabilitacao';
};

/** Os especiais separados por grupo, na ordem dos grupos; dentro de cada um, o mais recente primeiro. Grupo vazio não aparece. */
export function agruparEspeciais<T extends ResumoEspecialDTO>(lista: T[]): { grupo: (typeof GRUPOS_ESPECIAIS)[number]; itens: T[] }[] {
  return GRUPOS_ESPECIAIS
    .map((grupo) => ({
      grupo,
      itens: lista
        .filter((e) => grupoDoEspecial(e) === grupo.chave)
        .sort((a, b) => b.geradoEm.localeCompare(a.geradoEm)),
    }))
    .filter((g) => g.itens.length > 0);
}

/**
 * O título que sai no papel de um pedido. O parêntese no fim ("(Ildete)") existe
 * para distinguir duas vias do mesmo pedido na lista — na carta impressa, sobra.
 */
export const tituloDaCarta = (titulo: string): string => titulo.replace(/\s*\([^()]*\)\s*$/, '');

export interface RelatorioEspecialDTO extends ResumoEspecialDTO {
  /** O corpo, em Markdown restrito. */
  texto: string;
  fontes: { titulo: string; url: string }[];
  criadoEm: string;
  /** Com a identificação do hospital: só o que é para entregar à equipe a mostra. */
  paciente: PacienteNoPapel;
}

export interface Secao {
  titulo: Trecho[] | null;
  blocos: Bloco[];
}

/** Agrupa os blocos em seções: cada título de nível 1 ou 2 abre uma nova. */
export function seccionar(blocos: Bloco[]): Secao[] {
  const secoes: Secao[] = [];
  let atual: Secao | null = null;
  for (const b of blocos) {
    if (b.tipo === 'titulo' && b.nivel <= 2) {
      atual = { titulo: b.texto, blocos: [] };
      secoes.push(atual);
      continue;
    }
    if (!atual) {
      atual = { titulo: null, blocos: [] };
      secoes.push(atual);
    }
    atual.blocos.push(b);
  }
  return secoes;
}

/**
 * A seção das perguntas ganha a caixa verde — é o que a família leva na
 * visita, e é o mesmo destaque que ela tem no relatório de acompanhamento.
 */
export const ehSecaoDePerguntas = (s: Secao): boolean =>
  // So o titulo que COMECA falando de perguntas: "As medicacoes: uma lacuna
  // e uma pergunta" e uma secao comum, e ganhava a caixa por causa da palavra.
  s.titulo !== null && /^(?:\d+[.)]\s+)?(?:as\s+)?perguntas\b|^o que perguntar/i.test(textoPuro(s.titulo).trim());
